import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Plane, 
  Users, 
  Calendar, 
  TrendingUp, 
  LogOut, 
  Settings,
  Bell,
  DollarSign,
  BarChart3,
  UserCheck,
  AlertTriangle,
  Plus,
  Eye,
  FileText,
  MessageSquare,
  Key,
  BookOpen,
  Wallet,
  Radar,
  Heart,
  Anchor,
  ShieldAlert,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [recentTrips, setRecentTrips] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [modCount, setModCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    axios.get(`${API}/admin/community-moderation/count`).then((r) => setModCount(r.data.pending_count)).catch(() => {});
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const [statsRes, tripsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/trips/with-details`),
        axios.get(`${API}/notifications/payment-deadlines`)
      ]);

      setStats(statsRes.data);
      setRecentTrips(tripsRes.data.slice(0, 5)); // Show last 5 trips
      setNotifications(notificationsRes.data.notifications.slice(0, 3)); // Show top 3 notifications
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_e899d787-232a-49fb-887e-eb0377fd3380/artifacts/dy8a9fcf_trib%C3%B9%20in%20viaggio.png" 
                alt="Tribù in Viaggio" 
                className="h-10 sm:h-12 w-auto mr-3"
              />
              <h1 className="text-lg sm:text-xl font-bold text-slate-800">
                <span className="hidden sm:inline">Area Admin</span>
                <span className="sm:hidden">Admin</span>
              </h1>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/notifications">
                <Button variant="outline" size="sm" className="relative">
                  <Bell className="w-4 h-4" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </Button>
              </Link>
              <span className="text-xs sm:text-sm text-slate-600 hidden md:inline">
                {user?.first_name} {user?.last_name}
              </span>
              <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs sm:text-sm">
                Admin
              </Badge>
              <Link to="/change-password">
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  <span className="hidden md:inline">Password</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-2 text-sm sm:text-base px-2 sm:px-4"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
            Dashboard Amministratore
          </h2>
          <p className="text-sm sm:text-base text-slate-600">
            Panoramica completa di Tribù in Viaggio e controllo gestionale
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-blue-100">
                Viaggi Totali
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex items-center">
                <Plane className="h-6 w-6 sm:h-8 sm:w-8 text-blue-200 mr-3" />
                <div className="text-xl sm:text-2xl font-bold">{stats.total_trips || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-100">
                Utenti Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-8 w-8 text-green-200 mr-3" />
                <div className="text-2xl font-bold">{stats.total_users || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-100">
                Viaggi Confermati
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-purple-200 mr-3" />
                <div className="text-2xl font-bold">{stats.confirmed_trips || 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Azioni Amministrative</CardTitle>
            <CardDescription>
              Strumenti di gestione per amministratori
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/manage-trips">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
                  variant="outline"
                >
                  <Plus className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Gestisci Viaggi</div>
                    <div className="text-sm text-teal-600">Crea e modifica tutti i viaggi</div>
                  </div>
                </Button>
              </Link>

              <Link to="/users">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                  variant="outline"
                >
                  <UserCheck className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Gestisci Utenti</div>
                    <div className="text-sm text-blue-600">Admin, agenti e clienti</div>
                  </div>
                </Button>
              </Link>

              <Link to="/financial-reports">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                  variant="outline"
                >
                  <BarChart3 className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Report Finanziari</div>
                    <div className="text-sm text-purple-600">Analytics complete</div>
                  </div>
                </Button>
              </Link>

              <Link to="/commission-calculator">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                  variant="outline"
                >
                  <DollarSign className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calcolatore Commissioni</div>
                    <div className="text-sm text-green-600">Calcoli finanziari</div>
                  </div>
                </Button>
              </Link>

              <Link to="/deposit-calculator" data-testid="deposit-calculator-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                  variant="outline"
                >
                  <Wallet className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calcolatore Acconto Cliente</div>
                    <div className="text-sm text-amber-600">Acconto + volo da bonificare</div>
                  </div>
                </Button>
              </Link>

              <Link to="/travel-radar" data-testid="travel-radar-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                  variant="outline"
                >
                  <Radar className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Travel Radar AI</div>
                    <div className="text-sm text-indigo-600">Offerte aggregate + convenienza</div>
                  </div>
                </Button>
              </Link>

              <Link to="/gift-lists" data-testid="gift-lists-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100"
                  variant="outline"
                >
                  <Heart className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Liste Nozze / Regalo</div>
                    <div className="text-sm text-pink-600">Crea link condivisibile + gestisci contributi</div>
                  </div>
                </Button>
              </Link>

              <a href="/community" target="_blank" rel="noopener noreferrer" data-testid="community-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100"
                  variant="outline"
                >
                  <Anchor className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Community Crociere</div>
                    <div className="text-sm text-cyan-600">Link pubblico: fai conoscere i crocieristi</div>
                  </div>
                </Button>
              </a>

              <Link to="/community-moderation" data-testid="moderation-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 relative"
                  variant="outline"
                >
                  <ShieldAlert className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Moderazione Community</div>
                    <div className="text-sm text-rose-600">Approva i link inviati dai membri</div>
                  </div>
                  {modCount > 0 && (
                    <span className="absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center" data-testid="moderation-badge">
                      {modCount}
                    </span>
                  )}
                </Button>
              </Link>

              <Link to="/community-promotions" data-testid="promotions-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100"
                  variant="outline"
                >
                  <Tag className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Promozioni Crociere</div>
                    <div className="text-sm text-orange-600">Gestisci le offerte pubbliche</div>
                  </div>
                </Button>
              </Link>

              <Link to="/calendar">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100"
                  variant="outline"
                >
                  <Calendar className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Calendario Globale</div>
                    <div className="text-sm text-orange-600">Tutti i viaggi</div>
                  </div>
                </Button>
              </Link>

              <Link to="/quotes">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                  variant="outline"
                >
                  <FileText className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Preventivi</div>
                    <div className="text-sm text-indigo-600">Crea e gestisci preventivi</div>
                  </div>
                </Button>
              </Link>

              <Link to="/quote-requests">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
                  variant="outline"
                >
                  <MessageSquare className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Richieste Preventivo</div>
                    <div className="text-sm text-teal-600">Gestione preventivi clienti</div>
                  </div>
                </Button>
              </Link>

              <Link to="/notifications">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                  variant="outline"
                >
                  <AlertTriangle className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Centro Notifiche</div>
                    <div className="text-sm text-red-600">Scadenze e avvisi</div>
                  </div>
                </Button>
              </Link>

              <Link to="/tour-operators" data-testid="tour-operators-nav-link">
                <Button
                  className="flex items-center gap-3 h-auto p-4 w-full bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
                  variant="outline"
                >
                  <BookOpen className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Rubrica Tour Operator</div>
                    <div className="text-sm text-slate-600">Contatti, accessi e destinazioni</div>
                  </div>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Trips */}
          <Card>
            <CardHeader>
              <CardTitle>Viaggi Recenti</CardTitle>
              <CardDescription>
                Ultimi viaggi creati nell'agenzia
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentTrips.length > 0 ? recentTrips.map((tripData) => (
                  <div key={tripData.trip.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-800">{tripData.trip.title}</h4>
                      <p className="text-sm text-slate-600">{tripData.trip.destination}</p>
                      <p className="text-xs text-slate-500">
                        Cliente: {tripData.client?.first_name} {tripData.client?.last_name} | 
                        Agente: {tripData.agent?.first_name} {tripData.agent?.last_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={tripData.trip.status === 'active' ? 'default' : 'secondary'}
                        className="mb-2"
                      >
                        {tripData.trip.status}
                      </Badge>
                      <Link to={`/trips/${tripData.trip.id}`}>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )) : (
                  <p className="text-slate-500 text-center py-4">Nessun viaggio recente</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle>Notifiche Urgenti</CardTitle>
              <CardDescription>
                Avvisi e scadenze importanti
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.length > 0 ? notifications.map((notification) => (
                  <div key={notification.id} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-slate-800">{notification.title}</h4>
                      <p className="text-sm text-slate-600">{notification.message}</p>
                      <p className="text-xs text-slate-500">
                        Scade in {notification.days_until_due} giorni
                      </p>
                    </div>
                    <Badge 
                      variant={notification.priority === 'high' ? 'destructive' : 'secondary'}
                    >
                      {notification.priority}
                    </Badge>
                  </div>
                )) : (
                  <p className="text-slate-500 text-center py-4">Nessuna notifica urgente</p>
                )}
                {notifications.length > 0 && (
                  <Link to="/notifications">
                    <Button variant="outline" className="w-full">
                      Vedi tutte le notifiche
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;